import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, EmptyState } from "@/components/Layout";
import api, { soft } from "@/lib/api";
import Select from "@/components/Select";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";

const KIND_LABELS = {
  co_available_prepared_by:   "Întocmește raport CO disponibile",
  co_available_approved_by:   "Avizează raport CO disponibile",
  programming_org_manager:    "Manager structură (avizează programare CO)",
  programming_final_approver: "Aprobă programare CO",
};
const MANUAL_KINDS = ["co_available_prepared_by", "co_available_approved_by"];
const COMPUTED_KIND_HINTS = {
  programming_org_manager: 'Calculat automat din "Structură Organizatorică" (managerul definit pe fiecare unitate) — nu se configurează manual aici.',
  programming_final_approver: 'Calculat automat din "Angajator" (Ordonator de credite/Administrator societate) — nu se configurează manual aici.',
};

export default function Signatures() {
  const { has } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState({text:"",type:"info"});

  const load = async () => {
    try {
      const [s,u,o] = await Promise.all([
        api.get("/mdm/signatures"), soft(has("users.list") ? api.get("/users") : Promise.resolve({ data: [] }), []), soft(api.get("/mdm/org-units"), []),
      ]);
      setRows(s.data); setUsers(u.data); setUnits(o.data);
    } catch (err) {
      setMsg({text: err?.response?.data?.detail || "Eroare la încărcare", type:"error"});
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ kind: MANUAL_KINDS[0], user_id: "", title: "" });

  const save = async () => {
    if (!editing.user_id) return setMsg({text:"Selectează utilizatorul care semnează",type:"error"});
    if (!editing.title?.trim()) return setMsg({text:"Funcția (denumirea afișată în raport) este obligatorie",type:"error"});
    try {
      await api.post("/mdm/signatures", editing);
      setEditing(null); load();
      setMsg({text:"Semnătură creată.",type:"success"});
      setTimeout(()=>setMsg({text:""}), 2000);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const remove = async (id) => {
    if(!window.confirm("Confirmi eliminarea semnăturii?")) return;
    try { await api.delete(`/mdm/signatures/${id}`); load(); }
    catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const grouped = {};
  rows.forEach(s => { (grouped[s.kind] = grouped[s.kind] || []).push(s); });

  return (
    <>
      <PageHeader title="Semnături Autorizate" subtitle="Cine semnează rapoartele emise de platformă"
        actions={<button className="btn btn-primary" onClick={openNew} data-testid="add-signature" {...lock(has("signatures.create"))}>+ Semnătură nouă</button>} />
      <Toast msg={msg.text} type={msg.type} />

      <div style={{display:"grid",gap:16}}>
        {Object.entries(KIND_LABELS).map(([kind, label]) => {
          const isComputed = !!COMPUTED_KIND_HINTS[kind];
          return (
            <div key={kind} className="card fade-up" style={{padding:20}} data-testid={`sig-group-${kind}`}>
              <div style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase"}}>{label}</div>
              {isComputed && (
                <div style={{fontSize:11,color:"var(--sky-700)",marginTop:6,background:"var(--sky-100)",padding:"6px 10px",borderRadius:4}}>
                  ⓘ {COMPUTED_KIND_HINTS[kind]}
                </div>
              )}
              <div style={{marginTop:10,display:"grid",gap:6}}>
                {(grouped[kind]||[]).map(s => (
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#F8FAFC",borderRadius:6}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:600,color:"var(--navy)"}}>{s.title || "—"}</div>
                      <div style={{fontSize:12,color:"#64748B"}}>
                        {s.user_name || "—"}
                        {s.scope_org_unit_id && ` · Structură: ${units.find(u=>u.id===s.scope_org_unit_id)?.name || "?"}`}
                      </div>
                    </div>
                    {!s.computed && (
                      <button className="btn btn-danger" style={{padding:"3px 10px",fontSize:11}} onClick={()=>remove(s.id)} {...lock(has("signatures.deactivate"))}>Elimină</button>
                    )}
                  </div>
                ))}
                {(grouped[kind]||[]).length===0 && (
                  <div style={{color:"#94A3B8",fontSize:13}}>
                    {isComputed ? "Nimic de afișat încă — completează sursa de date corespunzătoare." : "Nicio semnătură definită."}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <Modal title="Adaugă semnătură autorizată" onClose={()=>setEditing(null)}>
          {users.length === 0 && (
            <div style={{padding:12, background:"#FEF3C7", color:"#92400E", borderRadius:6, marginBottom:14, fontSize:13}}>
              ⚠️ Nu există utilizatori. Creează întâi utilizatori în „Utilizatori & Roluri".
            </div>
          )}
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Tip semnătură</label>
              <Select className="select" value={editing.kind} onChange={e=>setEditing({...editing,kind:e.target.value})} data-testid="sig-kind">
                {MANUAL_KINDS.map(k=><option key={k} value={k}>{KIND_LABELS[k]}</option>)}
              </Select></div>
            <div><label className="label label-req">Utilizator care semnează</label>
              <Select className="select" value={editing.user_id||""} onChange={e=>setEditing({...editing,user_id:e.target.value})} data-testid="sig-user">
                <option value="">— alege utilizator —</option>
                {users.filter(u=>u.active).map(u=><option key={u.id} value={u.id}>{u.last_name} {u.first_name}</option>)}
              </Select></div>
            <div><label className="label label-req">Funcția (afișată în raport)</label>
              <input className="input" placeholder='ex: "Director General", "Șef Departament Resurse Umane"' value={editing.title} onChange={e=>setEditing({...editing,title:e.target.value})} data-testid="sig-title" /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-signature" {...lock(has("signatures.create"))}>Salvează</button>
          </div>
        </Modal>
      )}

      {rows.length === 0 && <EmptyState text="Nicio semnătură definită." />}
    </>
  );
}
